import { useMemo, useState } from "react"
import classNames from "classnames"

import type { PartnerAnalytics } from "./types"

import styles from "./index.module.scss"

interface PartnerLevelAnalyticsProps {
  partners: PartnerAnalytics[]
}

const isWebsiteUrl = (value: string) => /^https?:\/\//i.test(value)

const Chevron = () => (
  <svg className={styles.toggleIcon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
    <path d="M3.5 6.5L8 11l4.5-4.5" />
  </svg>
)

const renderSummaryStatValue = (value: string | number) => <span>{value}</span>

const renderLegacyPartnerCard = (partner: PartnerAnalytics) => (
  <div key={partner.name} className={styles.legacyCard}>
    <div className={styles.legacyTitle}>
      {partner.name} (
      {isWebsiteUrl(partner.source) ? (
        <a className={styles.sourceLink} href={partner.source} target="_blank" rel="noreferrer noopener">
          {partner.source}
        </a>
      ) : (
        partner.source
      )}
      )
    </div>
    <div className={styles.legacyGrid}>
      <div className={styles.legacyChannelCard}>
        <div className={styles.legacyChannelTitle}>Thermostat Device</div>
        <div className={classNames(styles.legacyDetailGrid, styles.legacyDetailGridSingle)}>
          <div className={styles.legacyDetailMetric}>
            <div className={styles.legacyDetailLabel}>Logo Clicks</div>
            <div className={styles.legacyDetailValue}>{partner.thermostatLogoClicks}</div>
          </div>
          <div className={styles.legacyDetailMetric}>
            <div className={styles.legacyDetailLabel}>QR Scans</div>
            <div className={styles.legacyDetailValue}>{partner.thermostatQrScans}</div>
          </div>
        </div>
      </div>
      <div className={styles.legacyChannelCard}>
        <div className={styles.legacyChannelTitle}>Mobile App</div>
        <div className={styles.legacyDetailGrid}>
          <div className={styles.legacyDetailMetric}>
            <div className={styles.legacyDetailLabel}>Logo Clicks</div>
            <div className={styles.legacyDetailValue}>{partner.mobileLogoClicks}</div>
          </div>
          <div className={styles.legacyDetailMetric}>
            <div className={styles.legacyDetailLabel}>Support Tab</div>
            <div className={styles.legacyDetailValue}>{partner.supportTabClicks}</div>
          </div>
          <div className={styles.legacyDetailMetric}>
            <div className={styles.legacyDetailLabel}>Website Visits</div>
            <div className={styles.legacyDetailValue}>{partner.websiteVisits}</div>
          </div>
          <div className={styles.legacyDetailMetric}>
            <div className={styles.legacyDetailLabel}>Request Tech</div>
            <div className={styles.legacyDetailValue}>{partner.requestTechClicks}</div>
          </div>
          <div className={classNames(styles.legacyDetailMetric, styles.legacyDetailMetricWide)}>
            <div className={styles.legacyDetailLabel}>Call/Text</div>
            <div className={styles.legacyDetailValue}>{partner.callTextActions}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
)

export const PartnerLevelAnalytics = ({ partners }: PartnerLevelAnalyticsProps) => {
  const detailedPartners = useMemo(() => partners.filter(partner => partner.sections?.length), [partners])
  const initialOpenPartner =
    detailedPartners.find(partner => partner.isExpandedByDefault)?.name ?? detailedPartners[0]?.name
  const [openPartner, setOpenPartner] = useState<string | undefined>(initialOpenPartner)

  if (!partners.length) {
    return null
  }

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h2 className={styles.title}>Partner-Level Analytics</h2>
          <div className={styles.subtitle}>
            Engagement and call responsiveness by trade. See which partners answer the leads you send them.
          </div>
        </div>
        <div className={styles.statsBadge}>
          <svg className={styles.statsBadgeIcon} viewBox="0 0 16 16" aria-hidden="true" focusable="false">
            <path d="M4 8h7" fill="none" stroke="currentColor" strokeLinecap="round" strokeWidth="1.8" />
            <path
              d="M8.5 5.5 11 8l-2.5 2.5"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.8"
            />
          </svg>
          Responsiveness added
        </div>
      </div>

      {detailedPartners.length ? (
        <div className={styles.accordionCardContainer}>
          {partners.map(partner => {
            const isOpen = openPartner ? openPartner === partner.name : Boolean(partner.isExpandedByDefault)
            const hasDetailSections = Boolean(partner.sections?.length)

            if (!hasDetailSections) {
              return renderLegacyPartnerCard(partner)
            }

            return (
              <div key={partner.name} className={styles.accordionCard}>
                <button
                  className={classNames(styles.accordionHeader, { [styles.accordionHeaderOpen]: isOpen })}
                  type="button"
                  onClick={() => setOpenPartner(isOpen ? undefined : partner.name)}
                >
                  <div className={styles.meta}>
                    <h3 className={styles.partnerTitle}>
                      {partner.name}{" "}
                      {partner.badge ? (
                        <span
                          className={classNames(styles.badge, {
                            [styles.badgeSuccess]: partner.badgeTone === "success",
                            [styles.badgeWarning]: partner.badgeTone === "warning",
                          })}
                        >
                          {partner.badge}
                        </span>
                      ) : null}
                    </h3>
                    <div className={styles.source}>
                      {isWebsiteUrl(partner.source) ? (
                        <a
                          className={styles.sourceLink}
                          href={partner.source}
                          target="_blank"
                          rel="noreferrer noopener"
                        >
                          {partner.source}
                        </a>
                      ) : (
                        partner.source
                      )}
                    </div>
                  </div>
                  <div className={styles.stats}>
                    {(partner.summaryStats ?? []).map(stat => (
                      <div key={stat.label} className={styles.stat}>
                        <div
                          className={classNames(styles.statValue, {
                            [styles.statValueSuccess]: stat.tone === "success",
                            [styles.statValueWarning]: stat.tone === "warning",
                          })}
                        >
                          {renderSummaryStatValue(stat.value)}
                        </div>
                        <div className={styles.statLabel}>{stat.label}</div>
                      </div>
                    ))}
                    <span
                      className={classNames(styles.toggle, { [styles.toggleOpen]: isOpen })}
                      aria-label={`${partner.name} details`}
                      aria-expanded={isOpen}
                    >
                      <Chevron />
                    </span>
                  </div>
                </button>

                {isOpen ? (
                  <div className={styles.body}>
                    <div className={styles.sectionGrid}>
                      {partner.sections?.map(section => {
                        const isResponsiveness = section.title === "Responsiveness"

                        return (
                          <div
                            key={section.title}
                            className={classNames(styles.sectionItem, {
                              [styles.sectionHalf]: section.width === "half",
                              [styles.sectionFull]: section.width === "full",
                            })}
                          >
                            <div className={styles.sectionTitle}>{section.title}</div>
                            {isResponsiveness ? (
                              <div className={styles.sectionList}>
                                {section.items.map(item => (
                                  <div key={item.label} className={styles.sectionStatRow}>
                                    <div className={styles.sectionStatLabel}>{item.label}</div>
                                    {item.progress != null ? (
                                      <div className={styles.sectionProgress}>
                                        <div className={styles.sectionProgressTrack}>
                                          <div
                                            className={styles.sectionProgressBar}
                                            style={{ width: `${item.progress}%` }}
                                          />
                                        </div>
                                        <div
                                          className={classNames(styles.sectionStatValue, {
                                            [styles.statValueSuccess]: item.progress >= 80,
                                            [styles.statValueWarning]: item.progress < 50,
                                          })}
                                        >
                                          {item.value}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className={styles.sectionStatValue}>{item.value}</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className={styles.sectionList}>
                                {section.items.map(item => (
                                  <div key={item.label} className={styles.sectionStatRow}>
                                    <div className={styles.sectionStatLabel}>{item.label}</div>
                                    <div className={styles.sectionStatValue}>{item.value}</div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : null}
    </section>
  )
}
